import * as Yup from 'yup'
import { AccountDetails } from '../../types'
import { isDateBeforeToday, isOneDateBeforeAnotherDate, WORD_OR_WHITESPACE } from '../../utils'
import { isDateAfterToday } from '../../utils/dateUtil'
import { isValidTime } from '../../utils/timeUtil'
import { ValidationLength, ValidationSymbolMaxLength } from './constants'
import { isFeeLengthValid } from './helpers'
import { MarketEnum, RouteTypeEnum, SettlementTypeEnum, TradeFormValues, TradeTypeEnum } from './Types'

const feeValidation = (negativesAllowed: boolean = false) =>
  Yup.number()
    .test('is-decimal', `Maximum value exceeded`, (value?: number) => isFeeLengthValid(value))
    .test(
      'is-non-negative',
      'Value must be >= 0',
      (value?: number) => value === undefined || negativesAllowed || value >= 0
    )

const validateQuantity = (tradeType: TradeTypeEnum, value?: number): { [key: string]: boolean } => {
  const lengthTest = !(
    value &&
    value?.toString().length > ValidationLength.quantityInteger &&
    tradeType === TradeTypeEnum.awayTrade
  )
  const integerTest = tradeType === TradeTypeEnum.awayTrade ? Number.isInteger(value) : true
  const splitValue = value?.toString().split('.')
  const testValue: boolean = splitValue
    ? splitValue[0]?.length > ValidationLength.quantityInteger ||
      splitValue[1]?.length > ValidationLength.quantityDecimal
    : false
  const decimalTest = tradeType !== TradeTypeEnum.awayTrade ? !testValue : true
  return {
    lengthTest,
    integerTest,
    decimalTest,
  }
}

const validateExecutionPrice = (value?: number): boolean => {
  const splitValue = value?.toString().split('.')
  const testValue: boolean = splitValue
    ? splitValue[0]?.length > ValidationLength.executionPriceInteger ||
      splitValue[1]?.length > ValidationLength.executionPriceDecimal
    : false
  return !testValue
}

const validateOrderId = (value?: string): { [key: string]: boolean } => {
  const lengthTest = !(value && value?.toString().length > ValidationLength.orderIdInteger)
  return {
    lengthTest,
  }
}

const validateExecutingBrokerLength = (value?: string): { [key: string]: boolean } => {
  const lengthTest = !(value && value?.toString().length > ValidationLength.executingBroker)
  return {
    lengthTest,
  }
}

export const tradeEntrySchema = (formik: { values: TradeFormValues } | undefined, accountDetails: AccountDetails) =>
  Yup.object().shape({
    repCode: Yup.string()
      .required('Required')
      .test('3-characters-long', 'Must be 3 characters', value => !!value && value.length === 3),
    symbol: Yup.string()
      .test('symbol-length', function (value) {
        const maxSymbolLength: number = ValidationSymbolMaxLength[formik!.values.securityCodeType]
        return value && value.length > maxSymbolLength
          ? this.createError({
              message: `Maximum length is ${maxSymbolLength} characters`,
              path: 'symbol',
            })
          : true
      })
      .matches(WORD_OR_WHITESPACE, 'Field does not accept special characters')
      .required('Required'),
    action: Yup.string().required('Required'),
    quantity: Yup.number()
      .required('Required')
      .test('is-integer', 'Must be a whole number', value => {
        const { integerTest } = validateQuantity(formik!.values.tradeType, value)
        return integerTest
      })
      .test('is-length', `Maximum value exceeded`, value => {
        const { lengthTest } = validateQuantity(formik!.values.tradeType, value)
        return lengthTest
      })
      .test('is-decimal', `Maximum value exceeded`, value => {
        const { decimalTest } = validateQuantity(formik!.values.tradeType, value)
        return decimalTest
      })
      .test('is-positive', 'Value must be > 0', (value?: number) => !!value && value > 0),
    executionPrice: Yup.number()
      .required('Required')
      .test('is-decimal', `Maximum value exceeded`, value => {
        return validateExecutionPrice(value)
      })
      .test('is-positive', 'Value must be > 0', (value?: number) => !!value && value > 0),
    tradeDate: Yup.string()
      .required('Required')
      .test('not-in-the-future', 'Date cannot be > today', value => !!value && !isDateAfterToday(value)),
    tradeTime: Yup.string().test('is-valid-time', 'Invalid Time Format (HH:MM:SS)', value => isValidTime(value)),
    settlementDate: Yup.string().when('isRequired', {
      is: () =>
        formik!.values.settlementType === SettlementTypeEnum.specialSettlement ||
        isDateBeforeToday(formik!.values.tradeDate) ||
        isOneDateBeforeAnotherDate(formik!.values.settlementDate, formik!.values.tradeDate),
      then: Yup.string()
        .required('Required')
        .test(
          'is-greater-than-or-equal-to-trade-date',
          'Must be > or = trade date',
          (settlementDate?: string): boolean =>
            !!settlementDate && !isOneDateBeforeAnotherDate(settlementDate, formik!.values.tradeDate)
        ),
    }),
    memo: Yup.string().max(20, `Maximum length is ${ValidationLength.memo} characters`),
    capacity: Yup.string().required('Required'),
    routeType: Yup.string()
      .when('tradeType', {
        is: TradeTypeEnum.execution,
        then: Yup.string().required('Required'),
      })
      .when('tradeType', {
        is: TradeTypeEnum.awayTrade,
        then: Yup.string().required('Required'),
      }),
    orderId: Yup.string().test(
      'is-length',
      `Maximum length is ${ValidationLength.orderIdInteger} characters`,
      value => {
        const { lengthTest } = validateOrderId(value)
        return lengthTest
      }
    ),
    executingBroker: Yup.string()
      .test('is-length', `Maximum length is ${ValidationLength.executingBroker} characters`, value => {
        const { lengthTest } = validateExecutingBrokerLength(value)
        return lengthTest
      })
      .when('tradeType', {
        is: TradeTypeEnum.awayTrade,
        then: Yup.string().when('market', {
          is: MarketEnum.Otc,
          then: Yup.string().required('Required'),
        }),
      }),
    sourceAccountNumber: Yup.string().when('tradeType', {
      is: TradeTypeEnum.allocation,
      then: Yup.string()
        .required('Required')
        .test(
          'different-from-accountNumber',
          'Cannot be same account',
          value => value !== accountDetails.accountNumber
        ),
    }),
    sourceAccountType: Yup.string().when('tradeType', {
      is: TradeTypeEnum.allocation,
      then: Yup.string().required('Required'),
    }),
    executionAlgorithm: Yup.string().when('routeType', {
      is: RouteTypeEnum.Algo,
      then: Yup.string().required('Required'),
    }),
    commission: feeValidation(true),
    markUpMarkDown: feeValidation(true),
    clientClearingFee: feeValidation(),
    generalPurposeFee: feeValidation(),
    liquidityFee: feeValidation(),
    tradeActivityFee: feeValidation(),
    lotMatchingInstructions: Yup.array()
      .of(
        Yup.object().shape({
          tradeDate: Yup.string()
            .required('Required')
            .test('is-in-the-past', 'Must be < today', value => (value ? isDateBeforeToday(value) : false)),
          quantity: Yup.number()
            .test(
              'is-decimal',
              `Max ${ValidationLength.lotMatchingQuantityInteger} digit whole number and ${ValidationLength.lotMatchingQuantityDecimal} decimal digits allowed`,
              (value?: number) => isFeeLengthValid(value)
            )
            .test('is-non-negative', 'Value must be > 0', (value?: number) => value === undefined || value > 0),
          price: Yup.number()
            .test(
              'is-decimal',
              `Max ${ValidationLength.lotMatchingPriceInteger} digit whole number and ${ValidationLength.lotMatchingPriceDecimal} decimal digits allowed`,
              (value?: number) => isFeeLengthValid(value)
            )
            .test('is-non-negative', 'Value must be > 0', (value?: number) => value === undefined || value > 0),
        })
      )
      .notRequired(),
  })
